import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLotCreateSchema, ClientLotUpdateSchema, LotUpdateOneSchema } from "api-types/lots/lotsRequests";
import type { LotBulkMoveResponse, ValidMoveTarget } from "api-types/lots/lotsResponses";
import type { Lot, LotCommunityPlan } from "api-types/modelTypes";
import type { OptionSetOverrides } from "building-types/options/optionTemplateTypes";
import { LAMDA_REQUEST_TIMEOUT, MAX_REQUEST_TIMEOUT, WEB_API_BASE_PATH } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import downloadFile from "common/utils/downloadFile";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import { handleFetchError } from "common/utils/fetchHelpers";
import { getLotBulkMoveURL, getLotValidMoveTargetsURL } from "lib/endpointHelpers";
import type { EmptyLot } from "lib/state/LotsContext";
import {
  deleteWithTimeout,
  fileUploadWithTimeout,
  getWithTimeout,
  postWithTimeout,
  putWithTimeout,
} from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

function baseUrl({ builderId, locationId }): string {
  return `${WEB_API_BASE_PATH}/builders/${builderId}/locations-internal/${locationId}/lots`;
}

export async function createLot(
  builderId: string,
  locationId: string,
  lot: ClientLotCreateSchema,
): StorageResponse<Lot> {
  try {
    const res = await postWithTimeout(`${baseUrl({ builderId, locationId })}`, { lot });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function createLots(builderId: string, locationId: string, lots: EmptyLot[]): StorageResponse<Lot[]> {
  try {
    const res = await postWithTimeout(`${baseUrl({ builderId, locationId })}/batch`, { lots });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function uploadLotsCSV(builderId: string, locationId: string, lotsCsv: File): StorageResponse<Lot[]> {
  try {
    const res = await fileUploadWithTimeout({
      path: `${baseUrl({ builderId, locationId })}/upload`,
      fileName: "csv",
      file: lotsCsv,
      timeout: MAX_REQUEST_TIMEOUT,
    });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function downloadLotsCSV(
  builderId: string,
  locationId: string,
  communityName: string,
): Promise<UserNotificationType | undefined> {
  try {
    const res = await getWithTimeout(`${baseUrl({ builderId, locationId })}/download`);
    const text = await res.text();

    if (!res.ok) {
      throw text;
    }

    // Trigger file download automatically
    downloadFile(`${communityName} - Lot List.csv`, text, "text/csv");

    return undefined;
  } catch (e) {
    return await handleFetchError(e);
  }
}

export async function updateLot(
  builderId: string,
  locationId: string,
  lotId: string,
  lot: LotUpdateOneSchema,
): Promise<[Lot, null] | [null, FetchErrorResponse]> {
  try {
    const res = await putWithTimeout(`${baseUrl({ builderId, locationId })}/${lotId}`, lot, LAMDA_REQUEST_TIMEOUT);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function updateLots(
  builderId: string,
  locationId: string,
  lots: ClientLotUpdateSchema[],
): StorageResponse<Lot> {
  try {
    const res = await putWithTimeout(`${baseUrl({ builderId, locationId })}/batch`, { lots }, LAMDA_REQUEST_TIMEOUT);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteLot(builderId: string, locationId: string, lotId: string): StorageResponse<string> {
  try {
    const res = await deleteWithTimeout(`${baseUrl({ builderId, locationId })}/${lotId}`);

    if (!res.ok) {
      throw new Error(`Failed to delete lot ${lotId}`);
    }

    return [lotId, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteLots(builderId: string, locationId: string, lotIds: string[]): StorageResponse<string[]> {
  try {
    const res = await deleteWithTimeout(`${baseUrl({ builderId, locationId })}/batch?ids=${lotIds.join(",")}`);
    const json = await res.json();

    if (!res.ok) {
      throw new Error(`Failed to delete lots for community ${locationId}`);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteAllLots(builderId: string, locationId: string): StorageResponse<string[]> {
  try {
    const res = await deleteWithTimeout(`${baseUrl({ builderId, locationId })}`);
    const json = await res.json();

    if (!res.ok) {
      throw new Error(`Failed to delete lots for community ${locationId}`);
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function createLotCommunityPlan(
  builderId: string,
  locationId: string,
  lotId: string,
  communityPlanId: string,
): StorageResponse<ClientLotCommunityPlanHydrated> {
  try {
    const res = await postWithTimeout(`${baseUrl({ builderId, locationId })}/${lotId}/lot-community-plans`, {
      communityPlanId,
    });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function updateLotCommunityPlan(input: {
  builderId: string;
  locationId: string;
  lotId: string;
  lotCommunityPlanId: string;
  lotCommunityPlanName?: string;
  basePrice?: number | undefined;
  externalId?: string | undefined;
  overrides?: OptionSetOverrides;
}): StorageResponse<ClientLotCommunityPlanHydrated> {
  try {
    const { builderId, locationId, lotId, lotCommunityPlanId, lotCommunityPlanName, basePrice, externalId, overrides } =
      input;
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/communities/${locationId}/lots/${lotId}/lot-community-plans/${lotCommunityPlanId}`,
      { name: lotCommunityPlanName, basePrice, externalId, overrides },
      30 * 1000,
    );
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function createLotCommunityPlans(
  builderId: string,
  locationId: string,
  communityPlanId: string,
  lotIds: string[],
): StorageResponse<LotCommunityPlan[]> {
  try {
    const res = await postWithTimeout(`${baseUrl({ builderId, locationId })}/plans`, {
      lotIds,
      communityPlanId,
    });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteLotCommunityPlans(
  builderId: string,
  locationId: string,
  communityPlanId: string,
  lotIds: string[],
): StorageResponse<LotCommunityPlan[]> {
  try {
    const res = await deleteWithTimeout(
      `${baseUrl({ builderId, locationId })}/plans?lotIds=${lotIds}&communityPlanId=${communityPlanId}`,
    );
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function deleteLotCommunityPlan(
  builderId: string,
  locationId: string,
  lotId: string,
  lotCommunityPlanId: string,
): StorageResponse<string> {
  try {
    const res = await deleteWithTimeout(
      `${baseUrl({ builderId, locationId })}/${lotId}/lot-community-plans/${lotCommunityPlanId}`,
    );

    if (!res.ok) {
      throw new Error(`Failed to delete lot community plan ${lotCommunityPlanId}`);
    }

    return [lotCommunityPlanId, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function getValidMoveTargets(
  builderId: string,
  locationId: string,
  communityId: string,
): StorageResponse<{ targets: ValidMoveTarget[] }> {
  try {
    const url = getLotValidMoveTargetsURL({ builderId, locationId, communityId });
    const res = await getWithTimeout(url);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function bulkMoveLots(
  builderId: string,
  locationId: string,
  lotIds: string[],
  targetCommunityId: string,
  options: { dryRun?: boolean; ignoreWarnings?: boolean } = {},
): StorageResponse<LotBulkMoveResponse> {
  try {
    const url = getLotBulkMoveURL({ builderId, locationId });
    const res = await postWithTimeout(url, {
      lotIds,
      targetCommunityId,
      dryRun: options.dryRun ?? false,
      ignoreWarnings: options.ignoreWarnings ?? true,
    });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}
